import {
    Combobox,
    ComboboxBody,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxItem,
    ComboboxItemIndicator,
    ComboboxItemLabel,
    ComboboxItemLeading,
    ComboboxList,
    type ComboboxOption,
} from '@plato/ui/components/combobox';
import { EmptyStateIcon, EmptyStateTitle } from '@plato/ui/components/empty-state';
import { Avatar } from '@plato/ui/primitives/avatar';
import { SearchIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { SubordinateUser } from '../../../server/types/infer-trpc-types.ts';
import { getInitials } from '../utils/get-initials.ts';

type ColleagueOption = ComboboxOption & {
    initials: string;
};

export type ForeignCalendarPickerProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    colleagues: SubordinateUser[];
    selectedUserId: string;
    onPick: (userId: string) => void;
};

/**
 * Picks whose calendar to show: a centred dialog on desktop, a full-screen sheet on
 * the phone. It has no field of its own, because the mobile avatar and the desktop
 * button both open it.
 */
export function ForeignCalendarPicker({
    open,
    onOpenChange,
    colleagues,
    selectedUserId,
    onPick,
}: ForeignCalendarPickerProps): ReactElement {
    const { t } = useTranslation();

    const options: ColleagueOption[] = colleagues.map((colleague) => ({
        value: colleague.id,
        label: `${colleague.firstName} ${colleague.lastName}`,
        initials: getInitials(colleague.firstName, colleague.lastName),
    }));
    const selectedOption = options.find((option) => option.value === selectedUserId);

    return (
        <Combobox<ColleagueOption>
            title={t('foreign_calendar', { ns: 'events' })}
            searchPlaceholder={t('foreign_calendar__search', { ns: 'events' })}
            items={options}
            value={selectedOption}
            onValueChange={(option) => {
                // undefined means the value was cleared, not that someone was picked
                if (option) {
                    onPick(option.value);
                }
            }}
            open={open}
            onOpenChange={onOpenChange}
        >
            <ComboboxContent>
                <ComboboxBody>
                    <ComboboxList<ColleagueOption>>
                        {(option) => (
                            <ComboboxItem key={option.value} value={option}>
                                <ComboboxItemLeading>
                                    <Avatar aria-hidden>{option.initials}</Avatar>
                                </ComboboxItemLeading>
                                <ComboboxItemLabel />
                                <ComboboxItemIndicator />
                            </ComboboxItem>
                        )}
                    </ComboboxList>
                    <ComboboxEmpty>
                        <EmptyStateIcon>
                            <SearchIcon />
                        </EmptyStateIcon>
                        <EmptyStateTitle>{t('nothing_found', { ns: 'common' })}</EmptyStateTitle>
                    </ComboboxEmpty>
                </ComboboxBody>
            </ComboboxContent>
        </Combobox>
    );
}
